import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useRef,
} from "react";
import Authenticator from "components/mfa/Authenticator";
import { apiPost } from "utils/apiFetch";
import apiPath from "utils/pathObj";

const MFAContext = createContext(null);

export const MFAProvider = ({ children }) => {
  const [modalState, setModalState] = useState({ isOpen: false, options: {} });
  const resolverRef = useRef(null);

  const requireMfa = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setModalState({ isOpen: true, options });
    });
  }, []);

  const closeModal = useCallback((result) => {
    setModalState({ isOpen: false, options: {} });
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  const handleVerify = async (code, { isRecoveryCode }) => {
    const endpoint = isRecoveryCode
      ? apiPath.mfaVerifyRecoveryCode
      : apiPath.mfaVerify;
    const body = isRecoveryCode
      ? { code, context: modalState.options.context }
      : { token: code, context: modalState.options.context };
    const { data } = await apiPost(endpoint, body);
    return {
      verified: !!data?.verified,
      message: data?.message,
      stepUpProof: data?.data?.stepUpProof,
    };
  };

  return (
    <MFAContext.Provider value={{ requireMfa }}>
      {children}
      <Authenticator
        isOpen={modalState.isOpen}
        onClose={() => closeModal(false)}
        onVerify={handleVerify}
        onSuccess={(result) =>
          closeModal({ verified: true, stepUpProof: result.stepUpProof })
        }
        allowRecoveryCode
        title={modalState.options.title}
        description={modalState.options.description}
      />
    </MFAContext.Provider>
  );
};

export const useMFA = () => {
  const ctx = useContext(MFAContext);
  if (!ctx) throw new Error("useMFA() must be used within <MFAProvider>.");
  return ctx;
};

export default MFAContext;
